import useTranslation from 'next-translate/useTranslation';
/* eslint-disable no-alert */
import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useRouter } from 'next/router';

import { hooks as supportHooks } from '@/domains/support';
import {
  Banner,
  Button,
  Input,
  Textarea,
  Modal,
  InformationModal,
  CheckboxWithTitle,
} from '@/ui/index';

import styles from './SupportBanner.module.scss';

const SupportBanner = ({ item, type = 'white' }) => {
  const router = useRouter();
  const { t } = useTranslation('common');
  const [commentMutation] = supportHooks.usePostComment();
  const FormSchema = Yup.object().shape({
    full_name: Yup.string().required(t('tariffSupport.fullName.placeHolder')),
    phone: Yup.string()
      .required(t('tariffSupport.number.placeHolder'))
      .length(12),
    email: Yup.string().required(t('tariffSupport.email.placeHolder')),
    agreement: Yup.boolean().oneOf(
      [true],
      t('tariffSupport.fullName.placeHolder'),
    ),
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [modalActive, setModalActive] = useState(false);

  React.useEffect(() => {
    if (!router.isReady) return;
    setModalActive(!!router.query.openModal);
  }, [router.isReady, router.query.openModal, router.pathname]);

  const formik = useFormik({
    initialValues: {
      full_name: '',
      phone: '',
      email: '',
      comment: '',
      agreement: true,
    },
    validationSchema: FormSchema,
    onSubmit(values) {
      commentMutation(values)
        .unwrap()
        .then(() => {
          setModalActive(false);
          formik.resetForm();
          setShowSuccess(true);
        });
    },
  });

  const onCloseModal = () => {
    formik.resetForm();
    setModalActive(false);
  };
  // console.log(formik.isValid);
  return (
    <>
      <Banner
        item={item}
        onClick={() => setModalActive(true)}
        type={type}
        itemScope
        itemType='https://schema.org/Offer'
      />

      <Modal
        active={modalActive}
        setActive={onCloseModal}
        itemScope
        itemType='https://schema.org/ContactPage'
      >
        <div className={'title'}>{t('tariffSupport.title')}</div>
        <div className={'note'}>{t('tariffSupport.desctription')}</div>
        <form
          className={`${styles.modalContent} grid-extend equal`}
          onSubmit={formik.handleSubmit}
        >
          <div className={`${styles.field} ${styles.half}`}>
            <Input
              onBlur={formik.handleBlur('full_name')}
              error={formik.touched.name && formik.errors.name}
              value={formik.values.name}
              onChange={(value) => formik.setFieldValue('full_name', value)}
              label={t('tariffSupport.fullName.label')}
              placeholder={t('tariffSupport.fullName.placeHolder')}
            />
          </div>

          <div className={`${styles.field} ${styles.half}`}>
            <Input
              onBlur={formik.handleBlur('phone')}
              error={formik.touched.phone && formik.errors.phone}
              value={formik.values.phone}
              type='tel'
              onChange={(value) => formik.setFieldValue('phone', value)}
              label={t('tariffSupport.number.label')}
              placeholderMask={'+994'}
              mask={'+\\9\\9\\4-99-999-99-99'}
            />
          </div>

          <div className={styles.field}>
            <Input
              onBlur={formik.handleBlur('email')}
              error={formik.touched.email && formik.errors.email}
              value={formik.values.email}
              type='email'
              onChange={(value) => formik.setFieldValue('email', value)}
              label={t('tariffSupport.email.label')}
              placeholder={t('tariffSupport.email.placeHolder')}
            />
          </div>

          <div className={`${styles.field} ${styles.textarea}`}>
            <Textarea
              onBlur={formik.handleBlur('comment')}
              error={formik.touched.comment && formik.errors.comment}
              value={formik.values.comment}
              onChange={(value) => formik.setFieldValue('comment', value)}
              label={t('tariffSupport.comment.label')}
              placeholder={t('tariffSupport.comment.placeHolder')}
            />
          </div>
          <div className={styles.checkboxContainer}>
            <CheckboxWithTitle
              reverse
              className={styles.checkbox}
              checked={formik.values.agreement}
              onChange={() =>
                formik.setFieldValue('agreement', !formik.values.agreement)
              }
            >
              <span
                dangerouslySetInnerHTML={{
                  __html: t('tariffSupport.accept'),
                }}
              ></span>
            </CheckboxWithTitle>
            {formik.touched.agreement && (
              <div className={styles.error}>{formik.errors.agreement}</div>
            )}
          </div>

          <div className={styles.field}>
            <Button
              className={styles.button}
              type={'submit'}
              disabled={!formik.isValid}
            >
              {t('tariffSupport.send')}
            </Button>
          </div>
        </form>
      </Modal>
      <InformationModal
        type={type}
        active={showSuccess}
        onClose={() => setShowSuccess(false)}
        title={t('tariffSupport.success.title')}
        description={t('tariffSupport.success.description')}
      />
    </>
  );
};

export default SupportBanner;
